import { useEffect } from "react";
import { useLog } from "../context/LogContext";
import { useRules } from "../context/RulesContext";
import { useSession } from "../context/SessionContext";
import { extractUser } from "../extractors";
import { userShape } from "../shapes";
import { UserDtoWithIncludes } from "../types";
import { useApi } from "./useApi";
import { useTracking } from "../context";

export const useUserQuery = () => {
  const api = useApi();
  const rules = useRules();
  const session = useSession();
  const log = useLog();
  const tracking = useTracking();

  const includes = [
    "userroles",
    "usergroups",
    "details",
    "customers",
    `specifications[${rules.application().get("userSpecifications").join(",")}]`,
  ];

  const userQuery = api.get.useQuery<
    UserDtoWithIncludes<
      ["userroles", "usergroups", "details", "customers", "specifications"]
    >,
    Omit<userShape, "refresh">
  >(
    "/users/current",
    {
      include: includes.join(","),
    },
    {
      enabled: session?.authenticated && !session?.sessionDeleted,
      select: (data) => ({
        ...extractUser(data),
        sso_provider: data.sso_provider,
        otp_module: data.otp_module,
      }),
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const user: userShape | undefined = userQuery.data
    ? { refresh: userQuery.refetch, ...userQuery.data }
    : undefined;

  const isAuthenticated = session?.authenticated && !session?.sessionDeleted;
  useEffect(() => {
    if (user && isAuthenticated) {
      log.setUser(user);
      tracking.setUser({ id: user.id, email: user.email });
    }
  }, [user, log, tracking, isAuthenticated]);

  return { ...userQuery, data: user };
};

export const useUser = () => {
  const { data } = useUserQuery();
  return data;
};
